import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { REVIEWS, COMPANY } from "@/data/site";

function Stars({ count, size = 14 }) {
  return (
    <div style={{ display: "inline-flex", gap: 3 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={size} strokeWidth={1.6}
          fill={i < count ? "var(--gold)" : "transparent"} color="var(--gold)" />
      ))}
    </div>
  );
}

export default function ReviewsSlider() {
  const [idx, setIdx] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const total = REVIEWS.length;
  const current = REVIEWS[idx];

  const go = (next) => {
    const n = ((next % total) + total) % total;
    setIdx(n);
    setAnimKey((k) => k + 1);
  };

  // Auto-advance every 8s (paused when interacted with)
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % total);
      setAnimKey((k) => k + 1);
    }, 8000);
    return () => clearInterval(t);
  }, [paused, total]);

  return (
    <div
      data-testid="reviews-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
        <span style={{ height: 1, background: "var(--gold)", width: 32 }} />
        <span className="eyebrow">Vélemények</span>
      </div>
      <div style={{
        display: "flex", alignItems: "center",
        gap: 16, rowGap: 14, flexWrap: "wrap", marginBottom: 88,
      }}>
        <Stars count={5} />
        <span aria-hidden style={{ width: 1, height: 18, background: "var(--line-2)", margin: "0 6px" }} />
        <span className="small" style={{ color: "var(--text-mid)" }}>
          Ügyfeleink visszajelzései
        </span>
      </div>

      {/* One large review */}
      <div
        style={{
          position: "relative",
          paddingTop: 56,
          minHeight: 280,
          maxWidth: 980,
        }}
        data-testid="reviews-current"
      >
        {/* Decorative quote glyph */}
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: 0, left: -6,
            fontFamily: "'DM Serif Display', Georgia, serif",
            fontSize: 110, lineHeight: 0.8,
            color: "var(--gold)", opacity: 0.32,
            pointerEvents: "none",
          }}
        >
          “
        </span>

        <div key={animKey} style={{ animation: "fadeUp 480ms cubic-bezier(.2,.7,.2,1) both", position: "relative", zIndex: 1 }}>
          <blockquote style={{
            margin: 0,
            fontFamily: "Manrope, sans-serif",
            fontWeight: 400,
            fontSize: "clamp(1.4rem, 2.6vw, 2rem)",
            lineHeight: 1.35,
            letterSpacing: "-0.018em",
            color: "var(--text)",
            maxWidth: 880,
          }}>
            {current.excerpt}
          </blockquote>

          <div style={{ marginTop: 36, display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
            <Stars count={current.rating} size={13} />
            <span style={{ width: 24, height: 1, background: "var(--line-2)" }} aria-hidden />
            <span style={{
              fontFamily: "Inter, sans-serif", fontWeight: 500,
              fontSize: 13, color: "var(--text)",
            }}>
              {current.name}
            </span>
            <span className="small" style={{ color: "var(--text-dim)" }}>
              · {current.date}
            </span>
          </div>
        </div>
      </div>

      {/* Controls: arrows + pagination + counter */}
      <div style={{
        marginTop: 56, paddingTop: 24,
        borderTop: "1px solid var(--line)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 24, flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <span className="eyebrow-muted">
            {String(idx + 1).padStart(2, "0")} <span style={{ color: "var(--text-faint)" }}>/ {String(total).padStart(2, "0")}</span>
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            {REVIEWS.map((_, i) => (
              <button
                key={i}
                onClick={() => go(i)}
                aria-label={`Vélemény ${i + 1}`}
                data-testid={`reviews-dot-${i}`}
                style={{
                  width: i === idx ? 24 : 8, height: 2,
                  background: i === idx ? "var(--gold)" : "var(--line-2)",
                  border: "none", padding: 0, cursor: "pointer",
                  transition: "width 280ms ease, background 280ms ease",
                }}
              />
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => go(idx - 1)}
            className="rev-arrow"
            data-testid="reviews-prev"
            aria-label="Előző"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={() => go(idx + 1)}
            className="rev-arrow"
            data-testid="reviews-next"
            aria-label="Következő"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
